class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';
    this.abortController = null;

    if (!this.input || !this.predictiveSearchResults) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    const closeButton = this.querySelector('button[type="button"]');
    const resetButton = this.querySelector('button[type="reset"]');

    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (closeButton) {
      closeButton.addEventListener('click', this.close.bind(this));
    }

    if (resetButton) {
      resetButton.addEventListener('click', this.clear.bind(this));
    }

    const debounceFn =
      window.debounce ||
      function (callback, delay) {
        let timeout;

        return function (...args) {
          clearTimeout(timeout);
          timeout = setTimeout(() => callback.apply(this, args), delay);
        };
      };

    this.input.addEventListener(
      'input',
      debounceFn((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    if (
      !this.searchTerm ||
      !newSearchTerm.toLowerCase().startsWith(this.searchTerm.toLowerCase())
    ) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const selectedOption = this.querySelector('[aria-selected="true"] a');

    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    if (selectedOption) {
      event.preventDefault();
      selectedOption.click();
    }
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]'
    );

    if (!searchForTextElement) return;
    if (!previousTerm) return;

    const currentButtonText = searchForTextElement.innerText;

    if (!currentButtonText) return;

    const escapedPreviousTerm = previousTerm.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    );

    const loosePattern = escapedPreviousTerm.replace(
      /[\s\-]+/g,
      '[\\s\\-]*'
    );

    const looseMatch = new RegExp(loosePattern, 'gi');

    searchForTextElement.innerText = currentButtonText.replace(
      looseMatch,
      newTerm
    );
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allElements = this.querySelectorAll(
      'li[role="option"], button[role="option"]'
    );

    if (!allElements.length) return;
    if (moveUp && !selectedElement) return;

    let activeElement = allElements[0];

    if (!moveUp && selectedElement) {
      activeElement =
        selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (!activeElement || activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', 'true');

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', 'false');
    }

    this.setLiveRegionText(activeElement.textContent.trim());

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id || ''
    );
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    /*
     * Force nine results.
     * This deliberately ignores any data-per-page="3"
     * value in the theme markup.
     */
    const perPage = 9;

    const queryKey = `${searchTerm
      .replace(/\s+/g, '-')
      .toLowerCase()}-${perPage}`;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);

      if (this.modal) {
        this.modal.classList.add('searching');
      }

      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const predictiveSearchUrl =
      theme?.routes?.predictive_search_url ||
      window.Shopify?.routes?.root + 'search/suggest' ||
      '/search/suggest';

    const url =
      `${predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}` +
      `&${encodeURIComponent('resources[type]')}=product,query,collection` +
      `&${encodeURIComponent('resources[limit]')}=${encodeURIComponent(perPage)}` +
      `&${encodeURIComponent('resources[limit_scope]')}=each` +
      `&section_id=predictive-search`;

    fetch(url, {
      signal: this.abortController.signal,
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parsedHTML = new DOMParser().parseFromString(
          text,
          'text/html'
        );

        const section = parsedHTML.querySelector(
          '#shopify-section-predictive-search'
        );

        if (!section) {
          this.close();
          return;
        }

        const resultsMarkup = section.innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] =
              resultsMarkup;
          }
        );

        this.renderSearchResults(resultsMarkup);

        if (this.modal) {
          this.modal.classList.add('searching');
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error?.code === 20) return;

        console.error('Predictive search failed:', error);
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text') ||
      'Loading';

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const resultCount = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (resultCount) {
      this.setLiveRegionText(resultCount.textContent.trim());
    }
  }

  getResultsMaxHeight() {
    const header =
      document.querySelector('.shopify-section-header') ||
      document.querySelector('.header') ||
      document.querySelector('.wsm-header-shell');

    if (!header) {
      this.resultsMaxHeight = window.innerHeight * 0.7;
      return this.resultsMaxHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      header.getBoundingClientRect().bottom -
      12;

    if (this.resultsMaxHeight < 240) {
      this.resultsMaxHeight = 420;
    }

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;

    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }

    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', 'false');

    this.removeAttribute('loading');
    this.removeAttribute('open');

    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    if (event) {
      event.preventDefault();
    }

    this.input.value = '';
    this.searchTerm = '';

    this.removeAttribute('open');
    this.removeAttribute('results');
    this.removeAttribute('loading');

    this.predictiveSearchResults.innerHTML = '';

    this.input.focus();

    if (this.modal) {
      this.modal.classList.remove('searching');
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}